import React, { useContext, useEffect, useRef } from "react";
import moment from "moment";
import { faArrowRight, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

// custom components
import { Lesson } from "types";
import ClassesContext from "pages/my_classes/MyClassesContext";
import ClassDetailsContext from "pages/class_details/ClassDetailsContext";
import ClassCardContent from "./ClassCardContent";
import ClassCardFooter from "./ClassCardFooter";
import CardRibbon from "./CardRibbon";

// styling
import {
  ClassCardContainer,
  ClassCardBody,
  ClassCardHeader,
  CardDate,
  CardTime,
  ClassCardBelow,
  ArrowContainer,
  NavigationArrow,
} from "./ClassCardStyle";

type ClassCardProps = {
  lesson: Lesson;
  section?: string;
  isStacked: boolean;
  onNextCardInStack: () => void;
  onPrevCardInStack: () => void;
  allowsBooking: boolean;
  hidesDate: boolean;
  hidesFooter: boolean;
  dynamicFooter: boolean;
  showRibbon: {
    completed: boolean;
    rated: boolean;
    past: boolean;
  };
};

function ClassCard({
  lesson,
  section,
  isStacked,
  onNextCardInStack,
  onPrevCardInStack,
  allowsBooking,
  hidesDate,
  hidesFooter,
  dynamicFooter,
  showRibbon,
}: ClassCardProps) {
  const classCardRef = useRef<HTMLDivElement>(null);
  const { setWidthToClassCard } = useContext(ClassesContext);
  const { setWidthToClassCardDetails } = useContext(ClassDetailsContext);

  useEffect(() => {
    if (classCardRef.current) {
      const width = classCardRef.current ? classCardRef.current.offsetWidth : 0;
      setWidthToClassCard(width);
    }
  }, [setWidthToClassCard]);

  useEffect(() => {
    if (classCardRef.current) {
      const width = classCardRef.current ? classCardRef.current.offsetWidth : 0;
      setWidthToClassCardDetails(width);
    }
  }, [setWidthToClassCardDetails]);

  // RENDERING COMPONENTS
  const renderDate = () => {
    if (hidesDate) {
      return null;
    }
    return (
      <CardDate dateTime={lesson.startTime} style={{ marginRight: 3 }}>
        {moment(new Date(lesson.date)).format("ddd, DD MMM YYYY ")}
      </CardDate>
    );
  };

  // TODO:
  // const renderStudiedTopic = () => {
  //   if (state.studied) {
  //     return <StudiedTopic>{t("You have studied this topic")}</StudiedTopic>;
  //   }
  //   return null;
  // };

  const checkGray = () => {
    // If this class card is not allowed to book, never gray it (eg: in Past Classes)
    if (!allowsBooking) return false;

    // Gray it if this class was booked
    // or its timeslot is overlapped with other booked class
    if (lesson?.bookedAt || lesson?.overlapped) return true;

    // Gray if this class is not booked but it's in the past
    if (moment().isAfter(moment(lesson.startTime))) return true;

    return false;
  };
  const gray = checkGray();

  const { completed, rated, startTime } = lesson;
  const showRatedRibbon = showRibbon.rated && rated;
  const showCompletedRibbon =
    showRibbon.completed && completed && !showRatedRibbon;
  const showPastRibbon =
    showRibbon.past &&
    moment().isAfter(moment(startTime)) &&
    !showRatedRibbon &&
    !showCompletedRibbon;

  const showARibbon = showCompletedRibbon || showRatedRibbon || showPastRibbon;
  const showDate = !hidesDate;

  return (
    <ClassCardContainer xs={24} sm={12} md={8} lg={6} xl={6}>
      {isStacked && (
        <>
          <ClassCardBelow>
            <div className="header" />
            <div className="body" />
            <div className="footer" />
          </ClassCardBelow>
          <ArrowContainer direction="left">
            <NavigationArrow
              icon={faArrowLeft as IconProp}
              onClick={onPrevCardInStack}
              direction="left"
            />
          </ArrowContainer>
          <ArrowContainer direction="right">
            <NavigationArrow
              icon={faArrowRight as IconProp}
              onClick={onNextCardInStack}
              direction="right"
            />
          </ArrowContainer>
        </>
      )}

      <ClassCardBody data-stacked={isStacked} ref={classCardRef}>
        <ClassCardHeader
          className="hoverable-target fade-bg"
          gray={gray}
          showARibbon={showARibbon}
          showDate={showDate}
        >
          {renderDate()}
          <CardTime dateTime={lesson.startTime}>{lesson.time}</CardTime>
        </ClassCardHeader>

        {/* RIBBON */}
        <CardRibbon lesson={lesson} showRibbon={showRibbon} />

        <ClassCardContent
          lesson={lesson}
          section={section}
          gray={gray}
          allowsBooking={allowsBooking}
        />
        {/* TODO: */}
        {/* {renderStudiedTopic()} */}
        <ClassCardFooter
          lesson={lesson}
          isStacked={false}
          onNextCardInStack={onNextCardInStack}
          onPrevCardInStack={onPrevCardInStack}
          hidesFooter={hidesFooter}
          dynamicFooter={dynamicFooter}
          gray={gray}
          section={section}
        />
      </ClassCardBody>
    </ClassCardContainer>
  );
}

export default ClassCard;
